/* eslint-disable @typescript-eslint/no-explicit-any */
// import { BreadCrumb } from "components/BreadCrumb";
import React, { useCallback, useEffect, useState } from "react";
import { useToaster } from "contexts/toasterContext";
import apiService from "services/apiService";
import { sortByKey } from "util/sortArrByName";
import Select from "react-select";
import * as yup from "yup";
import GeneratePayrollList, { PayrollListRow } from "./GeneratePayrollList";
import GeneratePayrollForm, { HeadOption } from "./GeneratePayrollForm";
import PayrollConfirmation, { GeneratedPayroll, PayrollDetail, formatINR, computePayrollTotals } from "./PayrollConfirmation";
import { FaTimes, FaDownload } from "react-icons/fa";

interface EmployeeOption {
    value: string;
    label: string;
    userId: number;
    designation?: string;
}

interface SalaryHeadRecord {
    id?: number;
    headType?: "Income Head" | "Deduction Head";
    account?: {
        id?: number;
        accountCode?: string;
        accountGroup?: string;
        disabled?: boolean;
    };
    headName?: string;
    createdOn?: string;
    status?: string;
}

interface PayrollDetailItem {
    salaryHead: SalaryHeadRecord;
    amount: number;
}

// const breadCrumbList = [
//     { name: "Generate Payroll", link: "/admin/generate-payroll" },
//     { name: "List", link: "#" },
// ];

// Month names (lowercase) — index maps directly to JS Date month index.
const MONTH_NAMES = [
    "january", "february", "march", "april", "may", "june",
    "july", "august", "september", "october", "november", "december",
];
const capitalizeWord = (value: string) => value.charAt(0).toUpperCase() + value.slice(1);

// Date/To Date are validated manually per calculation mode (they are derived
// from the month in MONTHLY mode), so they are not part of the yup schema.
const generatePayrollSchema = yup.object({
    selectedEmployee: yup.string().optional(),
    employeeName: yup.string().trim().required("Employee Name is required"),
    designation: yup.string().trim().required("Designation is required"),
    incomeHead: yup.string().optional(),
    incomeAmount: yup
        .number()
        .transform((value, originalValue) =>
            originalValue === "" || originalValue === null || originalValue === undefined
                ? undefined
                : value,
        )
        .typeError("Income Amount must be a number")
        .optional()
        .moreThan(0, "Income Amount must be greater than 0"),
});

// Full payroll record as returned by /api/payroll/list. Field names differ from
// the generate response (deductions / netPay / leaveDeduction).
interface PayrollRecord {
    id: number;
    userId?: number;
    employeeName: string;
    designation?: string;
    assessmentYear?: string;
    basicPay: number;
    totalEarning: number;
    deductions: number;
    leaveDeduction: number;
    incomeTaxTDS: number;
    reimbursement: number;
    leaves: number;
    netPay: number;
    fromDate: number;
    toDate: number;
    month: string;
    calculationMode: string;
    perDay: number;
    components: GeneratedPayroll["components"];
    details: PayrollDetail[];
}

// Normalises a list record into the shape PayrollConfirmation renders. Totals
// are recomputed there from components (falling back to these aggregate fields
// when a record has no components).
const mapRecordToGenerated = (rec: PayrollRecord): GeneratedPayroll => ({
    id: rec.id,
    userId: rec.userId ?? 0,
    employeeName: rec.employeeName,
    assessmentYear: rec.assessmentYear ?? "",
    month: rec.month,
    designation: rec.designation ?? "",
    fromDate: rec.fromDate,
    toDate: rec.toDate,
    calculationMode: rec.calculationMode,
    incomeTaxTDS: rec.incomeTaxTDS,
    basicPay: rec.basicPay,
    reimbursement: rec.reimbursement,
    leaves: rec.leaves,
    leaveRs: rec.leaveDeduction,
    totalEarning: rec.totalEarning,
    totalDeduction: rec.deductions,
    netSalary: rec.netPay,
    perDay: rec.perDay,
    details: rec.details ?? [],
    components: rec.components ?? [],
});

/**
 * Maps the /api/payroll/generate result to the PUT /api/payroll/{employeeId}
 * confirmation body. Field names differ from the generate response
 * (deductions / netPay / leaveDeduction), and basicPay + incomeTaxTDS are the
 * period amounts taken from the components (not the annual figures), so they
 * are derived here. Totals are taken from computePayrollTotals so they always
 * match what the review screen shows.
 */
const buildConfirmPayload = (data: GeneratedPayroll) => {
    const { totalEarning, totalDeduction, netSalary } = computePayrollTotals(data);
    const periodOf = (componentName: string) =>
        data.components?.find((component) => component.componentName === componentName)?.periodAmount ?? 0;

    return {
        id: data.id,
        paidDate: null,
        employeeCode: null,
        employeeName: data.employeeName,
        branchName: null,
        basicPay: periodOf("Basic Salary") || data.basicPay,
        totalEarning,
        deductions: totalDeduction,
        leaveDeduction: data.leaveRs ?? 0,
        incomeTaxTDS: periodOf("TDS") || data.incomeTaxTDS,
        reimbursement: data.reimbursement ?? 0,
        leaves: data.leaves ?? 0,
        netPay: netSalary,
        fromDate: data.fromDate,
        toDate: data.toDate,
        month: data.month,
        calculationMode: data.calculationMode,
        perDay: data.perDay,
        components: data.components ?? [],
        details: data.details ?? [],
    };
};

const GeneratePayroll: React.FC = () => {
    const { addToast } = useToaster();
    const [employeeOptions, setEmployeeOptions] = useState<EmployeeOption[]>([]);
    const [isLoadingEmployees, setIsLoadingEmployees] = useState(false);
    const [employeeSearch, setEmployeeSearch] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [employeePage, setEmployeePage] = useState(0);
    const [hasMoreEmployees, setHasMoreEmployees] = useState(true);
    const [salaryHeadRecords, setSalaryHeadRecords] = useState<SalaryHeadRecord[]>([]);
    const [isGenerating, setIsGenerating] = useState(false);
    const [selectedEmployee, setSelectedEmployee] = useState("");
    const [fromDate, setFromDate] = useState("2026-03-01");
    const [toDate, setToDate] = useState("2026-03-31");
    // MONTHLY → derive dates from month; DAILY → use the from/to date pickers.
    const [calculationMode, setCalculationMode] = useState<"MONTHLY" | "DAILY">("MONTHLY");
    const now = new Date();
    const [assessmentYear, setAssessmentYear] = useState(
        `${now.getFullYear()}-${String(now.getFullYear() + 1).slice(-2)}`,
    );
    const [selectedMonth, setSelectedMonth] = useState(MONTH_NAMES[now.getMonth()]);
    const [employeeName, setEmployeeName] = useState("");
    const [designation, setDesignation] = useState("");
    const [incomeHead, setIncomeHead] = useState("");
    const [incomeAmount, setIncomeAmount] = useState("");
    const [deductionHead, setDeductionHead] = useState("");
    const [deductionAmount, setDeductionAmount] = useState("");
    const [reimbursement, setReimbursement] = useState("0");
    const [leaves, setLeaves] = useState("0");
    const [perDay, setPerDay] = useState("0");
    const [formErrors, setFormErrors] = useState<Record<string, string>>({});
    const [payrollRows, setPayrollRows] = useState<PayrollListRow[]>([]);
    // Full records keyed by row id, and the record currently open in the viewer.
    const [payrollRecords, setPayrollRecords] = useState<Record<number, PayrollRecord>>({});
    const [viewPayroll, setViewPayroll] = useState<GeneratedPayroll | null>(null);
    const [payrollLoading, setPayrollLoading] = useState(false);
    const [showForm, setShowForm] = useState(false);
    const [activeEmployeeId, setActiveEmployeeId] = useState<number | null>(null);
    // Full-screen wizard form steps: 1 Employee → 2 Earnings & Deductions.
    // Reaching the 3rd step ("Review") triggers /generate and is driven by `phase`.
    const [wizardStep, setWizardStep] = useState<1 | 2>(1);
    // "form" while filling the wizard; "confirm" once /generate returns the
    // computed breakdown to review and confirm.
    const [phase, setPhase] = useState<"form" | "confirm">("form");
    const [generatedPayroll, setGeneratedPayroll] = useState<GeneratedPayroll | null>(null);
    const [isConfirming, setIsConfirming] = useState(false);

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(employeeSearch);
        }, 500);

        return () => clearTimeout(timer);
    }, [employeeSearch]);

    // Lock background scroll while any full-screen panel (workspace or viewer) is open.
    useEffect(() => {
        if (!showForm && !viewPayroll) return;
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.body.style.overflow = previousOverflow;
        };
    }, [showForm, viewPayroll]);

    // Open the read-only details viewer for the selected payroll row.
    const handleViewPayroll = (payrollId: number) => {
        const record = payrollRecords[payrollId];
        if (!record) {
            addToast("Payroll details are not available", "error");
            return;
        }
        setViewPayroll(mapRecordToGenerated(record));
    };

    const incomeHeadOptions = salaryHeadRecords.filter(
        (record) => record.headType === "Income Head"
    );
    const deductionHeadOptions = salaryHeadRecords.filter(
        (record) => record.headType === "Deduction Head"
    );

    const incomeHeadSelectOptions: HeadOption[] = incomeHeadOptions.map((head) => ({
        value: String(head.id),
        label: String(head.headName || ""),
    }));

    const deductionHeadSelectOptions: HeadOption[] = deductionHeadOptions.map((head) => ({
        value: String(head.id),
        label: String(head.headName || ""),
    }));

    const getSalaryHeadSelectStyles = (hasError = false) => ({
        control: (base: Record<string, unknown>) => ({
            ...base,
            minHeight: 40,
            height: 40,
            borderColor: hasError ? "#ef4444" : "#64748b",
            boxShadow: "none",
            backgroundColor: "transparent",
        }),
        valueContainer: (base: Record<string, unknown>) => ({
            ...base,
            paddingLeft: 12,
            paddingRight: 12,
            paddingTop: 0,
            paddingBottom: 0,
            height: 40,
        }),
        input: (base: Record<string, unknown>) => ({
            ...base,
            margin: 0,
            padding: 0,
        }),
        indicatorsContainer: (base: Record<string, unknown>) => ({
            ...base,
            height: 40,
        }),
        indicatorSeparator: (base: Record<string, unknown>) => ({
            ...base,
            display: "none",
        }),
        menuPortal: (base: Record<string, unknown>) => ({ ...base, zIndex: 9999 }),
    });


    const toEpochStart = (dateValue: string) =>
        new Date(`${dateValue}T00:00:00`).getTime();
    const toEpochEnd = (dateValue: string) =>
        new Date(`${dateValue}T23:59:59`).getTime();

    const getAssessmentYear = (dateValue: string) => {
        const year = new Date(dateValue).getFullYear();
        const nextYear = (year + 1).toString().slice(-2);
        return `${year}-${nextYear}`;
    };

    // ── MONTHLY dropdown options ────────────────────────────────────────────
    // Assessment year: current + past 4 years (e.g. "2026-27").
    const assessmentYearOptions = Array.from({ length: 5 }, (_, index) => {
        const yr = now.getFullYear() - index;
        const value = `${yr}-${String(yr + 1).slice(-2)}`;
        return { value, label: value };
    });

    // Month: for the current year only up to the current month; past years show all 12.
    const selectedYearNum = parseInt(assessmentYear.slice(0, 4), 10) || now.getFullYear();
    const monthUpperBound = selectedYearNum === now.getFullYear() ? now.getMonth() : 11;
    const monthOptions = MONTH_NAMES.slice(0, monthUpperBound + 1).map((name) => ({
        value: name,
        label: capitalizeWord(name),
    }));

    // Today (yyyy-mm-dd) — DAILY date pickers must not allow future dates.
    const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;

    const fetchPayrollList = useCallback(async () => {
        setPayrollLoading(true);
        try {
            const response = await apiService.get("/api/payroll/list");
            const rawData = response.data as
                | Record<string, unknown>[]
                | { data?: Record<string, unknown>[] }
                | undefined;

            const records = Array.isArray(rawData)
                ? rawData
                : Array.isArray(rawData?.data)
                    ? rawData.data
                    : [];

            // Keep the full records keyed by the row id so "View" can render the
            // complete breakdown (components/details) without another request.
            const recordsById: Record<number, PayrollRecord> = {};

            const mappedRows: PayrollListRow[] = records.map((item, index) => {
                const employeeId = Number(item.userId ?? item.employeeId ?? item.id ?? index + 1);
                // Row id is the payroll record id (unique per row) so multiple
                // payslips for the same employee map to distinct View targets.
                const rowId = Number(item.id ?? index + 1);
                const safeRowId = Number.isFinite(rowId) ? rowId : index + 1;
                recordsById[safeRowId] = item as unknown as PayrollRecord;
                return {
                    id: safeRowId,
                    employeeId: Number.isFinite(employeeId) ? employeeId : index + 1,
                    employeeName: String(item.employeeName ?? item.empName ?? item.displayName ?? "-"),
                    basicPay: String(item.basicPay ?? item.basic ?? item.grossPay ?? "-"),
                    deductions: String(item.deductions ?? item.totalDeduction ?? "-"),
                    netpay: String(item.netpay ?? item.netPay ?? item.netSalary ?? "-"),
                    fromdate: String(item.fromdate ?? item.fromDate ?? "-"),
                    toDate: String(item.toDate ?? item.todate ?? item.to_date ?? "-"),
                    month: String(item.month ?? "-"),
                    email: String(item.employeeEmail ?? item.email ?? ""),
                };
            });

            setPayrollRecords(recordsById);
            setPayrollRows(sortByKey(mappedRows, "employeeName"));
        } catch (error) {
            console.error("Error fetching payroll list:", error);
        } finally {
            setPayrollLoading(false);
        }
    }, []);

    useEffect(() => {
        apiService
            .get("/api/salary-heads/list")
            .then((response) => {
                const rawData = response.data as
                    | SalaryHeadRecord[]
                    | { data?: SalaryHeadRecord[] }
                    | undefined;

                const records = Array.isArray(rawData)
                    ? rawData
                    : Array.isArray(rawData?.data)
                        ? rawData.data
                        : [];

                setSalaryHeadRecords(records);
            })
            .catch((error) => {
                console.error("Error fetching salary heads:", error);
            });

        void fetchPayrollList();

    }, [fetchPayrollList]);

    const fetchEmployees = async (
        page: number,
        search: string,
        append = false,
    ) => {
        setIsLoadingEmployees(true);

        try {
            const response = await apiService.get(
                `/api/users?page=${page}&pageSize=15&search=${encodeURIComponent(search)}`
            );

            const content = response.data.content ?? [];
            const options: EmployeeOption[] = content.map((employee: any) => ({
                value: employee.email,
                label: employee.displayName || employee.email,
                userId: employee.id,
                designation: employee.designation,
            }));

            setEmployeeOptions((prev) =>
                append ? [...prev, ...options] : options
            );

            setEmployeePage(page);
            setHasMoreEmployees(!response.data.last);
        } catch (error) {
            console.error("Error fetching users:", error);
        } finally {
            setIsLoadingEmployees(false);
        }
    };

    useEffect(() => {
        fetchEmployees(0, debouncedSearch, false);
    }, [debouncedSearch]);

    const handlePayrollDownload = async (employeeId: number) => {
        try {
            const response = await apiService.get(`/api/payroll/download/${employeeId}`, {
                responseType: "blob",
            });

            const blob = new Blob([response.data], {
                type: response.headers["content-type"] || "application/octet-stream",
            });
            const url = URL.createObjectURL(blob);
            const anchor = document.createElement("a");
            anchor.href = url;
            anchor.download = `payroll-${employeeId}`;
            anchor.click();
            URL.revokeObjectURL(url);
            addToast("Payroll downloaded successfully", "success");
        } catch (error) {
            console.error("Failed to download payroll:", error);
            addToast("Failed to download payroll", "error");
        }
    };

    const resetGenerateFormValues = () => {
        setFromDate("2026-03-01");
        setToDate("2026-03-31");
        setIncomeHead("");
        setIncomeAmount("");
        setDeductionHead("");
        setDeductionAmount("");
        setReimbursement("0");
        setLeaves("0");
        setPerDay("0");
    };

    const handleOpenGenerateForm = () => {
        if (!selectedEmployee) {
            setFormErrors((prev) => ({ ...prev, selectedEmployee: "Employee is required" }));
            addToast("Please select an employee to generate form", "error");
            return;
        }

        resetGenerateFormValues();
        setFormErrors({});

        const selectedOption = employeeOptions.find((option) => option.value === selectedEmployee);
        if (selectedOption) {
            setEmployeeName(selectedOption.label || "");
            setDesignation(selectedOption.designation || "");
            setActiveEmployeeId(selectedOption.userId);
        }

        // Always start the wizard fresh at step 1 in the form phase.
        setWizardStep(1);
        setPhase("form");
        setGeneratedPayroll(null);
        setShowForm(true);
    };

    const handleEmployeeSelect = (email: string) => {
        const isEmployeeChanged = email !== selectedEmployee;

        if (isEmployeeChanged) {
            setFromDate("2026-03-01");
            setToDate("2026-03-31");
            setIncomeHead("");
            setIncomeAmount("");
            setDeductionHead("");
            setDeductionAmount("");
            setReimbursement("0");
            setLeaves("0");
            setPerDay("0");
            setFormErrors({});
            setShowForm(false);
            setActiveEmployeeId(null);
        }

        setSelectedEmployee(email);
        setFormErrors((prev) => ({ ...prev, selectedEmployee: "" }));
        const selectedOption = employeeOptions.find((option) => option.value === email);
        if (selectedOption) {
            setEmployeeName(selectedOption.label || "");
            setDesignation(selectedOption.designation || "");
            setFormErrors((prev) => ({ ...prev, employeeName: "", designation: "" }));
            return;
        }

        setEmployeeName("");
        setDesignation("");
    };

    const handleGeneratePayroll = async () => {
        const resolveEmployeeIdForGenerate = () => {
            if (activeEmployeeId) {
                return activeEmployeeId;
            }

            const selectedOption = employeeOptions.find((option) => option.value === selectedEmployee);
            if (selectedOption?.userId) {
                return selectedOption.userId;
            }

            const normalizedFormEmployeeName = employeeName.trim().toLowerCase();
            const matchedRow = payrollRows.find(
                (row) => row.employeeName.trim().toLowerCase() === normalizedFormEmployeeName,
            );

            return matchedRow?.employeeId ?? null;
        };

        const resolvedEmployeeId = resolveEmployeeIdForGenerate();

        // Common (mode-independent) fields validated via the schema.
        const nextErrors: Record<string, string> = {};
        try {
            await generatePayrollSchema.validate(
                { employeeName, designation, incomeHead, incomeAmount },
                { abortEarly: false }
            );
        } catch (error) {
            if (error instanceof yup.ValidationError) {
                error.inner.forEach((validationError) => {
                    if (validationError.path && !nextErrors[validationError.path]) {
                        nextErrors[validationError.path] = validationError.message;
                    }
                });
            }
        }

        // Mode-specific date/period handling.
        //  • MONTHLY → dates derived from the selected assessment year + month.
        //  • DAILY   → dates come from the from/to date pickers; month is empty.
        let fromEpoch = 0;
        let toEpoch = 0;
        let monthValue = "";
        let assessmentYearValue = "";

        if (calculationMode === "MONTHLY") {
            if (!assessmentYear) nextErrors.assessmentYear = "Assessment Year is required";
            if (!selectedMonth) nextErrors.month = "Month is required";
            if (!nextErrors.assessmentYear && !nextErrors.month) {
                const yearNum = parseInt(assessmentYear.slice(0, 4), 10);
                const monthIndex = MONTH_NAMES.indexOf(selectedMonth);
                // First day 00:00:00 → last day 23:59:59 of the selected month.
                fromEpoch = new Date(yearNum, monthIndex, 1, 0, 0, 0, 0).getTime();
                toEpoch = new Date(yearNum, monthIndex + 1, 0, 23, 59, 59, 0).getTime();
                monthValue = selectedMonth;
                assessmentYearValue = assessmentYear;
            }
        } else {
            if (!fromDate) nextErrors.fromDate = "From Date is required";
            if (!toDate) nextErrors.toDate = "To Date is required";
            else if (fromDate && new Date(toDate).getTime() < new Date(fromDate).getTime()) {
                nextErrors.toDate = "To Date must be on or after From Date";
            }
            if (!nextErrors.fromDate && !nextErrors.toDate) {
                fromEpoch = toEpochStart(fromDate);
                toEpoch = toEpochEnd(toDate);
                monthValue = "";
                assessmentYearValue = getAssessmentYear(fromDate);
            }
        }

        if (Object.keys(nextErrors).length > 0) {
            setFormErrors(nextErrors);
            addToast("Please fill all required fields", "error");
            return;
        }
        setFormErrors({});

        if (!resolvedEmployeeId) {
            addToast("Unable to determine employee for payroll generation", "error");
            return;
        }

        const selectedIncomeHead = incomeHeadOptions.find(
            (record) => String(record.id) === incomeHead
        );
        const selectedDeductionHead = deductionHeadOptions.find(
            (record) => String(record.id) === deductionHead
        );

        const details: PayrollDetailItem[] = [];

        if (selectedIncomeHead && Number(incomeAmount) > 0) {
            details.push({
                salaryHead: selectedIncomeHead,
                amount: Number(incomeAmount),
            });
        }

        if (selectedDeductionHead && Number(deductionAmount) > 0) {
            details.push({
                salaryHead: selectedDeductionHead,
                amount: Number(deductionAmount),
            });
        }

        const basePayload = {
            userId: resolvedEmployeeId,
            employeeName,
            designation,
            fromDate: fromEpoch,
            toDate: toEpoch,
            assessmentYear: assessmentYearValue,
            reimbursement: Number(reimbursement || 0),
            month: monthValue,
            leaves: Number(leaves || 0),
            calculationMode,
        };

        const payload = details.length > 0 ? { ...basePayload, details } : basePayload;

        try {
            setIsGenerating(true);
            const response = await apiService.post(`/api/payroll/generate`, payload);

            if (response?.status === 200) {
                // Move to the review/confirm step with the computed breakdown.
                // Nothing is persisted yet — that happens on explicit confirm.
                const generated = (response?.data?.data ?? null) as GeneratedPayroll | null;
                if (generated) {
                    setGeneratedPayroll(generated);
                    setPhase("confirm");
                    addToast("Payroll generated. Please review and confirm.", "success");
                } else {
                    addToast("Payroll generated but no details were returned", "error");
                }
            }
        } catch (error) {
            console.error("Failed to generate payroll:", error);
            addToast("Failed to generate payroll", "error");
        } finally {
            setIsGenerating(false);
        }
    };

    // ── Step 1 validation for the wizard "Next" gating ──────────────────────
    // (Step 2 → "Generate & Review" reuses the full validation in
    //  handleGeneratePayroll, so no separate gate is needed there.)
    const validateEmployeeStep = (): Record<string, string> => {
        const errs: Record<string, string> = {};
        if (!employeeName.trim()) errs.employeeName = "Employee Name is required";
        if (!designation.trim()) errs.designation = "Designation is required";
        if (calculationMode === "MONTHLY") {
            if (!assessmentYear) errs.assessmentYear = "Assessment Year is required";
            if (!selectedMonth) errs.month = "Month is required";
        } else {
            if (!fromDate) errs.fromDate = "From Date is required";
            if (!toDate) errs.toDate = "To Date is required";
            else if (fromDate && new Date(toDate).getTime() < new Date(fromDate).getTime()) {
                errs.toDate = "To Date must be on or after From Date";
            }
        }
        return errs;
    };

    const handleWizardNext = () => {
        const errs = validateEmployeeStep();
        if (Object.keys(errs).length > 0) {
            setFormErrors(errs);
            addToast("Please fill all required fields", "error");
            return;
        }
        setFormErrors({});
        setWizardStep(2);
    };

    const handleWizardBack = () => {
        setFormErrors({});
        setWizardStep(1);
    };

    /*  const handleGenerateAllPayroll = () => {
         navigate("/admin/generate-payroll/run", {
             state: { selectedEmployeeEmail: selectedEmployee || null },
         });
     }; */

    const handleSavePage = () => {
        const payload = {
            selectedEmployee,
            fromDate,
            toDate,
            employeeName,
            designation,
            incomeHead,
            incomeAmount,
            deductionHead,
            deductionAmount,
            reimbursement,
            leaves,
            perDay,
            savedAt: new Date().toISOString(),
        };

        localStorage.setItem("generatePayrollDraft", JSON.stringify(payload));

        const saveWithPicker = async () => {
            try {
                type SaveFilePicker = (options: {
                    suggestedName?: string;
                    types?: Array<{
                        description?: string;
                        accept: Record<string, string[]>;
                    }>;
                }) => Promise<{
                    name: string;
                    createWritable: () => Promise<{
                        write: (data: string) => Promise<void>;
                        close: () => Promise<void>;
                    }>;
                }>;

                const picker = (
                    window as unknown as { showSaveFilePicker?: SaveFilePicker }
                ).showSaveFilePicker;

                if (!picker) {
                    const blob = new Blob([JSON.stringify(payload, null, 2)], {
                        type: "application/json",
                    });
                    const url = URL.createObjectURL(blob);
                    const anchor = document.createElement("a");
                    anchor.href = url;
                    anchor.download = `generate-payroll-${Date.now()}.json`;
                    anchor.click();
                    URL.revokeObjectURL(url);
                    return;
                }

                const handle = await picker({
                    suggestedName: `generate-payroll-${Date.now()}.json`,
                    types: [
                        {
                            description: "JSON file",
                            accept: { "application/json": [".json"] },
                        },
                        {
                            description: "CSV file",
                            accept: { "text/csv": [".csv"] },
                        },
                        {
                            description: "Text file",
                            accept: { "text/plain": [".txt"] },
                        },
                    ],
                });

                const writable = await handle.createWritable();
                const fileName = String(handle.name || "").toLowerCase();

                if (fileName.endsWith(".csv")) {
                    const csvRows = Object.entries(payload).map(
                        ([key, value]) => `${key},"${String(value ?? "").replace(/"/g, '""')}"`
                    );
                    await writable.write(`field,value\n${csvRows.join("\n")}`);
                } else if (fileName.endsWith(".txt")) {
                    const textData = Object.entries(payload)
                        .map(([key, value]) => `${key}: ${String(value ?? "")}`)
                        .join("\n");
                    await writable.write(textData);
                } else {
                    await writable.write(JSON.stringify(payload, null, 2));
                }

                await writable.close();
            } catch (error) {
                const domError = error as DOMException;

                // User canceling the picker is expected behavior.
                if (domError?.name === "AbortError") {
                    return;
                }

                console.error("Failed to save payroll file:", error);
            }
        };

        void saveWithPicker();
    };

    const handlePrintPage = () => {
        window.print();
    };

    const decreaseAmountByFifty = (
        amount: string,
        setAmount: React.Dispatch<React.SetStateAction<string>>
    ) => {
        const parsedAmount = Number(amount);
        const safeAmount = Number.isFinite(parsedAmount) ? parsedAmount : 0;
        const updatedAmount = Math.max(0, safeAmount - 50);
        setAmount(String(updatedAmount));
    };

    const renderFieldError = (field: string) => (
        <p
            className={`mt-1 text-sm min-h-[20px] ${formErrors[field] ? "text-red-500" : "text-transparent"
                }`}
        >
            {formErrors[field] || "Validation placeholder"}
        </p>
    );

    const handleCloseGenerateFormModal = () => {
        resetGenerateFormValues();
        setShowForm(false);
        setFormErrors({});
        setActiveEmployeeId(null);
        setWizardStep(1);
        setPhase("form");
        setGeneratedPayroll(null);
    };

    // Final confirmation of the generated payroll →
    // PUT /api/payroll/{employeeId}. The path id is the SELECTED employee's id
    // (activeEmployeeId), falling back to the userId echoed on the generated
    // payroll so the correct employee is always targeted.
    const handleConfirmPayroll = async () => {
        if (!generatedPayroll) return;

        const employeeId = activeEmployeeId ?? generatedPayroll.userId;
        if (!employeeId) {
            addToast("Unable to determine employee for confirmation", "error");
            return;
        }

        try {
            setIsConfirming(true);
            const payload = buildConfirmPayload(generatedPayroll);
            await apiService.put(`/api/payroll/${employeeId}`, payload);
            addToast("Payroll confirmed and submitted successfully", "success");
            handleCloseGenerateFormModal();
            await fetchPayrollList();
        } catch (error) {
            console.error("Failed to confirm payroll:", error);
            const err = error as { response?: { data?: { message?: string } } };
            addToast(err?.response?.data?.message || "Failed to confirm payroll", "error");
        } finally {
            setIsConfirming(false);
        }
    };

    const filteredPayrollRows = payrollRows;

    return (
        <div className="text-slate-900 dark:text-white bg-white dark:bg-gray-800 transition-colors duration-300">
            <div className="bg-white dark:bg-gray-800">
                <GeneratePayrollList
                    rows={filteredPayrollRows}
                    loading={payrollLoading}
                    onSave={handlePayrollDownload}
                    onView={handleViewPayroll}
                    // Employee select + Generate Form button live on the
                    // table's search row, aligned to the right end.
                    headerActions={
                        <div className="flex items-start gap-3">
                            <div className="w-[300px]">
                                <Select
                                    inputId="employee"
                                    options={employeeOptions}
                                    value={
                                        employeeOptions.find(
                                            (option) => option.value === selectedEmployee
                                        ) || null
                                    }
                                    onChange={(selectedOption) =>
                                        handleEmployeeSelect(selectedOption?.value || "")
                                    }
                                    placeholder="--Select Employee Name--"
                                    isSearchable
                                    isClearable
                                    isLoading={isLoadingEmployees}
                                    classNamePrefix="employee-select"
                                    menuPortalTarget={
                                        typeof window !== "undefined" ? document.body : null
                                    }
                                    onInputChange={(value, meta) => {
                                        if (meta.action === "input-change") {
                                            setEmployeeSearch(value);
                                        }
                                    }}
                                    onMenuScrollToBottom={() => {
                                        if (hasMoreEmployees && !isLoadingEmployees) {
                                            fetchEmployees(employeePage + 1, employeeSearch, true);
                                        }
                                    }}
                                    filterOption={null}
                                    // Pin the control to a fixed 40px height so the
                                    // loading spinner / indicators can't grow it while
                                    // employees load — this is what caused the toolbar
                                    // (and the whole tab) to shift down then back up.
                                    styles={{
                                        control: (base) => ({
                                            ...base,
                                            minHeight: 40,
                                            height: 40,
                                            boxShadow: "none",
                                            borderColor: formErrors.selectedEmployee
                                                ? "#ef4444"
                                                : base.borderColor,
                                        }),
                                        valueContainer: (base) => ({
                                            ...base,
                                            height: 40,
                                            padding: "0 8px",
                                            display: "flex",
                                            alignItems: "center",
                                            flexWrap: "nowrap",
                                        }),
                                        // Keep the placeholder / selected value on a single
                                        // centered line. While employees load the loading
                                        // indicator narrows the value area; without this the
                                        // placeholder wrapped to a 2nd line and spilled out of
                                        // the fixed-height control.
                                        placeholder: (base) => ({
                                            ...base,
                                            whiteSpace: "nowrap",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            maxWidth: "100%",
                                        }),
                                        singleValue: (base) => ({
                                            ...base,
                                            whiteSpace: "nowrap",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            maxWidth: "100%",
                                        }),
                                        input: (base) => ({
                                            ...base,
                                            margin: 0,
                                            padding: 0,
                                        }),
                                        indicatorsContainer: (base) => ({
                                            ...base,
                                            height: 40,
                                        }),
                                        menuPortal: (base) => ({
                                            ...base,
                                            zIndex: 9999,
                                        }),
                                    }}
                                />
                                {/* {renderFieldError("selectedEmployee")} */}
                            </div>

                            <button
                                type="button"
                                onClick={handleOpenGenerateForm}
                                // Disabled if loading employees OR if no employee is selected
                                disabled={isLoadingEmployees || !selectedEmployee}
                                // Applied solid blue styling directly and added disabled state fading
                                className="h-[42px] rounded-md bg-blue-600 px-5 text-sm font-medium text-white transition-all hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:bg-blue-600 whitespace-nowrap"
                            >
                                Generate Form
                            </button>
                        </div>
                    }
                />
            </div>

            {showForm && (
                <div className="fixed inset-0 z-[60] flex flex-col bg-slate-100 dark:bg-gray-900">
                    {/* ── Workspace header + stepper ── */}
                    <header className="shrink-0 border-b border-slate-200 dark:border-slate-700 bg-white dark:bg-gray-800">
                        <div className="w-full px-4 md:px-6 lg:px-8">
                            <div className="flex items-center justify-between gap-3 py-3">
                                <div className="min-w-0">
                                    <h2 className="truncate text-base font-bold text-slate-900 dark:text-white md:text-lg">
                                        {phase === "confirm" ? "Review & Confirm Payroll" : "Generate Payroll"}
                                    </h2>
                                    <p className="truncate text-xs text-slate-500 dark:text-slate-400">
                                        {employeeName || "—"}
                                        {designation ? ` · ${designation}` : ""}
                                    </p>
                                </div>
                                <button
                                    type="button"
                                    onClick={handleCloseGenerateFormModal}
                                    aria-label="Close payroll workspace"
                                    className="flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-500 transition-colors hover:bg-slate-100 dark:hover:bg-slate-700"
                                >
                                    <FaTimes className="h-4 w-4" />
                                </button>
                            </div>

                            {/* Step indicator: Employee → Earnings & Deductions → Review & Generate */}
                            <ol className="flex items-center gap-2 pb-3">
                                {[
                                    { id: 1, label: "Employee" },
                                    { id: 2, label: "Earnings & Deductions" },
                                    { id: 3, label: "Review & Confirm" },
                                ].map((s, index, arr) => {
                                    const completed = phase === "confirm" ? true : wizardStep > s.id;
                                    const active = phase === "confirm" ? s.id === 3 : wizardStep === s.id;
                                    return (
                                        <li key={s.id} className="flex items-center gap-2">
                                            <span
                                                className={`flex h-6 w-6 items-center justify-center rounded-full text-xs font-bold ${active
                                                    ? "bg-blue-700 text-white"
                                                    : completed
                                                        ? "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300"
                                                        : "bg-slate-200 text-slate-500 dark:bg-slate-700 dark:text-slate-400"
                                                    }`}
                                            >
                                                {completed && !active ? "✓" : s.id}
                                            </span>
                                            <span
                                                className={`hidden text-xs font-medium sm:inline ${active ? "text-slate-900 dark:text-white" : "text-slate-500 dark:text-slate-400"}`}
                                            >
                                                {s.label}
                                            </span>
                                            {index < arr.length - 1 && (
                                                <span className="mx-1 h-px w-5 bg-slate-200 dark:bg-slate-700 sm:w-10" />
                                            )}
                                        </li>
                                    );
                                })}
                            </ol>
                        </div>
                    </header>

                    {/* ── Body (scrollable) ── */}
                    <div className="flex-1 overflow-y-auto">
                        <div className="w-full px-4 md:px-6 lg:px-8 py-5">
                            {phase === "form" ? (
                                <div className="mx-auto w-full max-w-4xl">
                                    <GeneratePayrollForm
                                        step={wizardStep}
                                        employeeName={employeeName}
                                        designation={designation}
                                        fromDate={fromDate}
                                        toDate={toDate}
                                        maxDate={todayStr}
                                        calculationMode={calculationMode}
                                        assessmentYear={assessmentYear}
                                        month={selectedMonth}
                                        assessmentYearOptions={assessmentYearOptions}
                                        monthOptions={monthOptions}
                                        onCalculationModeChange={(mode) => {
                                            setCalculationMode(mode);
                                            setFormErrors((prev) => ({
                                                ...prev,
                                                fromDate: "",
                                                toDate: "",
                                                assessmentYear: "",
                                                month: "",
                                            }));
                                        }}
                                        onAssessmentYearChange={(value) => {
                                            setAssessmentYear(value);
                                            setFormErrors((prev) => ({ ...prev, assessmentYear: "" }));
                                            // Keep the month valid: the current year only exposes
                                            // months up to the current one.
                                            const yearNum = parseInt(value.slice(0, 4), 10);
                                            const upper = yearNum === now.getFullYear() ? now.getMonth() : 11;
                                            if (MONTH_NAMES.indexOf(selectedMonth) > upper) {
                                                setSelectedMonth(MONTH_NAMES[upper]);
                                            }
                                        }}
                                        onMonthChange={(value) => {
                                            setSelectedMonth(value);
                                            setFormErrors((prev) => ({ ...prev, month: "" }));
                                        }}
                                        incomeHead={incomeHead}
                                        incomeAmount={incomeAmount}
                                        deductionHead={deductionHead}
                                        deductionAmount={deductionAmount}
                                        reimbursement={reimbursement}
                                        leaves={leaves}
                                        isGenerating={isGenerating}
                                        formErrors={formErrors}
                                        incomeHeadSelectOptions={incomeHeadSelectOptions}
                                        deductionHeadSelectOptions={deductionHeadSelectOptions}
                                        onEmployeeNameChange={(value) => {
                                            setEmployeeName(value);
                                            setFormErrors((prev) => ({ ...prev, employeeName: "" }));
                                        }}
                                        onDesignationChange={(value) => {
                                            setDesignation(value);
                                            setFormErrors((prev) => ({ ...prev, designation: "" }));
                                        }}
                                        onFromDateChange={(value) => {
                                            setFromDate(value);
                                            setFormErrors((prev) => ({ ...prev, fromDate: "" }));
                                        }}
                                        onToDateChange={(value) => {
                                            setToDate(value);
                                            setFormErrors((prev) => ({ ...prev, toDate: "" }));
                                        }}
                                        onIncomeHeadChange={(value) => {
                                            setIncomeHead(value);
                                            setFormErrors((prev) => ({ ...prev, incomeHead: "" }));
                                        }}
                                        onIncomeAmountChange={(value) => {
                                            setIncomeAmount(value);
                                            setFormErrors((prev) => ({ ...prev, incomeAmount: "" }));
                                        }}
                                        onDeductionHeadChange={setDeductionHead}
                                        onDeductionAmountChange={setDeductionAmount}
                                        onReimbursementChange={setReimbursement}
                                        onLeavesChange={setLeaves}
                                        onGenerate={handleGeneratePayroll}
                                        onPrint={handlePrintPage}
                                        onSave={handleSavePage}
                                        onDecreaseIncome={() => decreaseAmountByFifty(incomeAmount, setIncomeAmount)}
                                        onDecreaseDeduction={() => decreaseAmountByFifty(deductionAmount, setDeductionAmount)}
                                        getSalaryHeadSelectStyles={getSalaryHeadSelectStyles}
                                        renderFieldError={renderFieldError}
                                    />
                                </div>
                            ) : (
                                generatedPayroll && (
                                    // Editable review: admins can adjust basic, TDS, and every
                                    // amount before confirming. Edits flow back into
                                    // generatedPayroll so buildConfirmPayload submits them.
                                    <PayrollConfirmation
                                        data={generatedPayroll}
                                        editable
                                        onChange={setGeneratedPayroll}
                                    />
                                )
                            )}
                        </div>
                    </div>

                    {/* ── Footer actions ── */}
                    <footer className="shrink-0 border-t border-slate-200 dark:border-slate-700 bg-white dark:bg-gray-800">
                        <div className="flex w-full items-center justify-between gap-3 px-4 md:px-6 lg:px-8 py-3">
                            {phase === "confirm" ? (
                                <>
                                    <button
                                        type="button"
                                        onClick={() => setPhase("form")}
                                        disabled={isConfirming}
                                        className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-5 py-2.5 text-sm font-semibold text-slate-600 dark:text-slate-200 transition-all hover:bg-slate-100 dark:hover:bg-slate-700 disabled:cursor-not-allowed disabled:opacity-60"
                                    >
                                        ← Back to Edit
                                    </button>
                                    <div className="flex items-center gap-4">
                                        {generatedPayroll && (
                                            <div className="hidden text-right sm:block">
                                                <p className="text-[11px] uppercase tracking-wide text-slate-400">Net Payable</p>
                                                <p className="text-base font-bold tabular-nums text-blue-700 dark:text-blue-400">
                                                    {formatINR(computePayrollTotals(generatedPayroll).netSalary)}
                                                </p>
                                            </div>
                                        )}
                                        <button
                                            type="button"
                                            onClick={handleConfirmPayroll}
                                            disabled={isConfirming}
                                            className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-blue-700 px-6 py-2.5 text-sm font-semibold text-white shadow-sm transition-all hover:bg-blue-800 disabled:cursor-not-allowed disabled:opacity-60"
                                        >
                                            {isConfirming ? "Submitting…" : "Confirm & Submit"}
                                        </button>
                                    </div>
                                </>
                            ) : (
                                <>
                                    {wizardStep > 1 ? (
                                        <button
                                            type="button"
                                            onClick={handleWizardBack}
                                            className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-5 py-2.5 text-sm font-semibold text-slate-600 dark:text-slate-200 transition-all hover:bg-slate-100 dark:hover:bg-slate-700"
                                        >
                                            ← Back
                                        </button>
                                    ) : (
                                        <button
                                            type="button"
                                            onClick={handleCloseGenerateFormModal}
                                            className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-5 py-2.5 text-sm font-semibold text-slate-600 dark:text-slate-200 transition-all hover:bg-slate-100 dark:hover:bg-slate-700"
                                        >
                                            Cancel
                                        </button>
                                    )}
                                    {wizardStep === 1 ? (
                                        <button
                                            type="button"
                                            onClick={handleWizardNext}
                                            className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-blue-700 px-6 py-2.5 text-sm font-semibold text-white shadow-sm transition-all hover:bg-blue-800"
                                        >
                                            Next: Earnings &amp; Deductions →
                                        </button>
                                    ) : (
                                        <button
                                            type="button"
                                            onClick={handleGeneratePayroll}
                                            disabled={isGenerating}
                                            className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-blue-700 px-6 py-2.5 text-sm font-semibold text-white shadow-sm transition-all hover:bg-blue-800 disabled:cursor-not-allowed disabled:opacity-60"
                                        >
                                            {isGenerating ? "Generating…" : "Generate & Review →"}
                                        </button>
                                    )}
                                </>
                            )}
                        </div>
                    </footer>
                </div>
            )}

            {/* ── Read-only payroll details viewer (opened from the list) ── */}
            {viewPayroll && (
                <div className="fixed inset-0 z-[60] flex flex-col bg-slate-100 dark:bg-gray-900">
                    <header className="shrink-0 border-b border-slate-200 dark:border-slate-700 bg-white dark:bg-gray-800">
                        <div className="flex w-full items-center justify-between gap-3 px-4 md:px-6 lg:px-8 py-3">
                            <div className="min-w-0">
                                <h2 className="truncate text-base font-bold text-slate-900 dark:text-white md:text-lg">
                                    Payroll Details
                                </h2>
                                <p className="truncate text-xs text-slate-500 dark:text-slate-400">
                                    {viewPayroll.employeeName}
                                    {viewPayroll.month ? ` · ${capitalizeWord(viewPayroll.month)}` : ""}
                                </p>
                            </div>
                            <button
                                type="button"
                                onClick={() => setViewPayroll(null)}
                                aria-label="Close payroll details"
                                className="flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-500 transition-colors hover:bg-slate-100 dark:hover:bg-slate-700"
                            >
                                <FaTimes className="h-4 w-4" />
                            </button>
                        </div>
                    </header>

                    <div className="flex-1 overflow-y-auto">
                        <div className="w-full px-4 md:px-6 lg:px-8 py-5">
                            <PayrollConfirmation data={viewPayroll} />
                        </div>
                    </div>

                    <footer className="shrink-0 border-t border-slate-200 dark:border-slate-700 bg-white dark:bg-gray-800">
                        <div className="flex w-full items-center justify-end gap-3 px-4 md:px-6 lg:px-8 py-3">
                            <button
                                type="button"
                                onClick={() => setViewPayroll(null)}
                                className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-5 py-2.5 text-sm font-semibold text-slate-600 dark:text-slate-200 transition-all hover:bg-slate-100 dark:hover:bg-slate-700"
                            >
                                Close
                            </button>
                            <button
                                type="button"
                                onClick={() => handlePayrollDownload(viewPayroll.id)}
                                className="inline-flex items-center justify-center gap-2 rounded-lg bg-blue-700 px-6 py-2.5 text-sm font-semibold text-white shadow-sm transition-all hover:bg-blue-800"
                            >
                                <FaDownload className="h-4 w-4" /> Download Payslip
                            </button>
                        </div>
                    </footer>
                </div>
            )}

            <style>
                {`@media print {
          @page {
            margin: 12mm;
          }

          body * {
            visibility: hidden !important;
          }

          .generate-payroll-print-area,
          .generate-payroll-print-area * {
            visibility: visible !important;
          }

          .generate-payroll-print-area {
            position: absolute !important;
            left: 0 !important;
            top: 0 !important;
            width: 100% !important;
            margin: 0 !important;
          }

          .no-print {
            display: none !important;
          }

          .generate-payroll-print-area {
            border: 0 !important;
            background: #ffffff !important;
            color: #111827 !important;
            box-shadow: none !important;
            padding: 0 !important;
          }

          .generate-payroll-print-area input,
          .generate-payroll-print-area select {
            background: #ffffff !important;
            color: #111827 !important;
            border: 1px solid #cbd5e1 !important;
          }

          .generate-payroll-print-area .bg-sky-500 {
            background: #0ea5e9 !important;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }

                    .employee-select__control {
                        min-height: 42px;
                    }
                }

                .dark .employee-select__control {
                    background-color: #334155 !important;
                    border-color: #475569 !important;
                }

                .dark .employee-select__single-value,
                .dark .employee-select__input-container,
                .dark .employee-select__placeholder {
                    color: #e2e8f0 !important;
                }

                .dark .employee-select__menu,
                .dark .employee-select__menu-list {
                    background-color: #1e293b !important;
                    color: #e2e8f0 !important;
                }

                .dark .employee-select__option {
                    background-color: #1e293b !important;
                    color: #e2e8f0 !important;
                }

                .dark .employee-select__option--is-focused,
                .dark .employee-select__option--is-selected {
                    background-color: #334155 !important;
                    color: #ffffff !important;
                }

                .dark .employee-select__indicator,
                .dark .employee-select__clear-indicator {
                    color: #cbd5e1 !important;
                }

                .salary-head-select__control {
                    min-height: 42px;
                    border-color: #94a3b8 !important;
                }

                .salary-head-select__indicator-separator {
                    display: none !important;
                }

                .salary-head-select__placeholder,
                .salary-head-select__single-value {
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                }

                .dark .salary-head-select__control {
                    background-color: #334155 !important;
                    border-color: #475569 !important;
                }

                .dark .salary-head-select__single-value,
                .dark .salary-head-select__input-container,
                .dark .salary-head-select__placeholder {
                    color: #e2e8f0 !important;
                }

                .dark .salary-head-select__menu,
                .dark .salary-head-select__menu-list {
                    background-color: #1e293b !important;
                    color: #e2e8f0 !important;
                }

                .dark .salary-head-select__option {
                    background-color: #1e293b !important;
                    color: #e2e8f0 !important;
                }

                .dark .salary-head-select__option--is-focused,
                .dark .salary-head-select__option--is-selected {
                    background-color: #334155 !important;
                    color: #ffffff !important;
                }

                .dark .salary-head-select__indicator,
                .dark .salary-head-select__clear-indicator {
                    color: #cbd5e1 !important;
                }

                .dark .generate-payroll-print-area input[type="date"] {
                    color-scheme: dark;
                    background-color: #334155 !important;
                    border-color: #475569 !important;
                    color: #e2e8f0 !important;
                }

                .dark .generate-payroll-print-area input[type="date"]::-webkit-calendar-picker-indicator {
                    filter: invert(0.9);
                    opacity: 0.9;
                    cursor: pointer;
                }

                .dark .generate-payroll-print-area input[type="date"]::-webkit-datetime-edit,
                .dark .generate-payroll-print-area input[type="date"]::-webkit-datetime-edit-text,
                .dark .generate-payroll-print-area input[type="date"]::-webkit-datetime-edit-month-field,
                .dark .generate-payroll-print-area input[type="date"]::-webkit-datetime-edit-day-field,
                .dark .generate-payroll-print-area input[type="date"]::-webkit-datetime-edit-year-field {
                    color: #e2e8f0;
                }
                `}
            </style>
        </div>
    );
};

export default GeneratePayroll;
